import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { User, Mail, Edit2, Check, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ProfileSettings({ user: initialUser }) {
  const { t } = useLanguage();
  const [user, setUser] = useState(initialUser);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingRole, setIsEditingRole] = useState(false);
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [role, setRole] = useState(user?.role || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setUser(initialUser);
    setFullName(initialUser?.full_name || '');
    setRole(initialUser?.role || '');
  }, [initialUser]);

  const handleSaveName = async () => {
    if (!fullName.trim()) {
      setError('Full name cannot be empty');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.put(`${API}/auth/profile`, {
        full_name: fullName
      });

      const updatedUser = response.data;
      setUser(updatedUser);
      setIsEditingName(false);
      
      // Update localStorage so the change persists
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Dispatch event to update parent components
      window.dispatchEvent(new CustomEvent('userUpdated', { 
        detail: { user: updatedUser } 
      }));
      
      toast.success('Profile updated!', {
        description: 'Your full name has been saved successfully.',
        duration: 3000
      });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update profile';
      setError(message);
      toast.error('Update failed', {
        description: message,
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRole = async () => {
    if (!role.trim()) {
      setError('Role cannot be empty');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.put(`${API}/auth/profile`, {
        role: role
      });

      const updatedUser = response.data;
      setUser(updatedUser);
      setIsEditingRole(false);
      
      // Update localStorage so the change persists
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Dispatch event to update parent components
      window.dispatchEvent(new CustomEvent('userUpdated', { 
        detail: { user: updatedUser } 
      }));
      
      toast.success('Role updated!', {
        description: 'Your role has been saved successfully.',
        duration: 3000
      });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update role';
      setError(message);
      toast.error('Update failed', {
        description: message,
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelName = () => {
    setFullName(user?.full_name || '');
    setIsEditingName(false);
    setError('');
  };

  const handleCancelRole = () => {
    setRole(user?.role || '');
    setIsEditingRole(false);
    setError('');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('profileSettings')}</h1>
        <p className="text-gray-600 mt-2">Manage your personal information</p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Personal Information
          </CardTitle>
          <CardDescription>
            Your email and profile details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Email (Read-only) */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email Address
            </Label>
            <Input
              type="email"
              value={user?.email || ''}
              disabled
              className="bg-gray-50 text-gray-600 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500">
              Email cannot be changed
            </p>
          </div>

          {/* Full Name (Editable) */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <User className="w-4 h-4" />
              {t('fullName')}
            </Label>
            {isEditingName ? (
              <div className="space-y-3">
                <Input
                  type="text"
                  placeholder={t('fullName')}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="bg-white border-gray-200 focus:border-blue-400 focus:ring-blue-400"
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleSaveName}
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {loading ? t('saving') : t('save')}
                  </Button>
                  <Button
                    onClick={handleCancelName}
                    variant="outline"
                    disabled={loading}
                  >
                    <X className="w-4 h-4 mr-2" />
                    {t('cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div>
                  {user?.full_name ? (
                    <p className="text-gray-900 font-medium">{user.full_name}</p>
                  ) : (
                    <p className="text-gray-500 italic">{t('noNameSet')}</p>
                  )}
                </div>
                <Button
                  onClick={() => setIsEditingName(true)}
                  variant="outline"
                  size="sm"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  {user?.full_name ? t('edit') : t('addName')}
                </Button>
              </div>
            )}
            {!isEditingName && (
              <p className="text-xs text-gray-500">
                {user?.full_name 
                  ? 'Click edit to change your full name'
                  : 'Add your full name to personalize your profile'}
              </p>
            )}
          </div>

          {/* Role (Editable) */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              {t('role')}
            </Label>
            {isEditingRole ? (
              <div className="space-y-3">
                <Input
                  type="text"
                  placeholder={t('role')}
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="bg-white border-gray-200 focus:border-blue-400 focus:ring-blue-400"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleSaveRole}
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {loading ? t('saving') : t('save')}
                  </Button>
                  <Button
                    onClick={handleCancelRole}
                    variant="outline"
                    disabled={loading}
                  >
                    <X className="w-4 h-4 mr-2" />
                    {t('cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div>
                  <p className="text-gray-900 font-medium">{user?.role || t('noRoleSet')}</p>
                </div>
                <Button
                  onClick={() => setIsEditingRole(true)}
                  variant="outline"
                  size="sm"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  {t('edit')}
                </Button>
              </div>
            )}
            {!isEditingRole && (
              <p className="text-xs text-gray-500">
                Click edit to change your role
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ProfileSettings;
